import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MessagesSquare, Loader2, AlertTriangle, CheckCircle2, Target } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const confidenceColor = {
  HIGH: 'bg-green-600',
  MEDIUM: 'bg-amber-500',
  LOW: 'bg-red-600',
};

const formatEuro = (n) =>
  typeof n === 'number'
    ? new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
    : n;

const daysSince = (dateStr) => {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
};

const NegotiationIntelligence = ({ property }) => {
  const [loading, setLoading] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [error, setError] = useState(null);

  const runStrategy = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/ai/negotiation-intelligence`, {
        property_type: property.propertyType || property.type || 'house',
        bedrooms: property.bedrooms || 0,
        location: property.location?.area || property.location?.fullAddress || 'Dublin',
        asking_price: property.price || 0,
        days_on_market: daysSince(property.dateAdded),
        listing_type: property.type || 'sale',
        country: property.location?.country || null,
      });
      setStrategy(data.strategy);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="negotiation-intelligence">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessagesSquare className="h-5 w-5 text-[#7042cc]" />
          Negotiation Intelligence
        </CardTitle>
        <CardDescription>
          Strategic opening offer, counter-offer strategy and walk-away price from a buyer's-agent perspective.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!strategy && !loading && (
          <Button
            onClick={runStrategy}
            data-testid="run-negotiation-btn"
            className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
          >
            Get negotiation strategy
          </Button>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-gray-600" data-testid="negotiation-loading">
            <Loader2 className="h-5 w-5 animate-spin" />
            Building strategy…
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {strategy && !strategy.error && (
          <div className="space-y-4" data-testid="negotiation-results">
            <div className="flex items-center gap-3">
              <Badge
                className={`${confidenceColor[strategy.negotiation_confidence] || 'bg-gray-600'} text-white px-3 py-1 text-sm`}
                data-testid="negotiation-confidence"
              >
                {strategy.negotiation_confidence} confidence
              </Badge>
            </div>

            {/* Price ladder */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <PriceTile
                icon={<Target className="h-4 w-4 text-[#7042cc]" />}
                label="Opening offer"
                value={formatEuro(strategy.opening_offer)}
                sub={`${strategy.opening_offer_pct_below_ask}% below ask`}
                testid="neg-opening"
              />
              <PriceTile
                icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                label="Target close"
                value={formatEuro(strategy.target_purchase_price)}
                testid="neg-target"
              />
              <PriceTile
                icon={<AlertTriangle className="h-4 w-4 text-red-600" />}
                label="Walk-away price"
                value={formatEuro(strategy.walk_away_price)}
                testid="neg-walkaway"
              />
            </div>

            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Opening rationale</div>
              <p className="text-sm text-gray-700" data-testid="neg-opening-rationale">
                {strategy.opening_rationale}
              </p>
            </div>

            <div>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Counter-offer strategy</div>
              <p className="text-sm text-gray-700" data-testid="neg-counter-strategy">
                {strategy.counter_offer_strategy}
              </p>
            </div>

            {Array.isArray(strategy.leverage_points) && strategy.leverage_points.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Leverage points</div>
                <ul className="space-y-1">
                  {strategy.leverage_points.map((l, i) => (
                    <li key={i} className="text-sm text-gray-700 flex gap-2">
                      <Target className="h-4 w-4 text-[#7042cc] flex-shrink-0 mt-0.5" />
                      {l}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {Array.isArray(strategy.red_flags_to_check) && strategy.red_flags_to_check.length > 0 && (
              <div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Red flags to check</div>
                <ul className="space-y-1">
                  {strategy.red_flags_to_check.map((r, i) => (
                    <li key={i} className="text-sm text-gray-700 flex gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="text-xs text-gray-500 pt-2 border-t border-gray-100">
              AI-generated negotiation guidance. Always conduct independent due diligence before making offers.
            </div>
          </div>
        )}

        {strategy?.error && (
          <div className="text-sm text-red-700">{strategy.error}</div>
        )}
      </CardContent>
    </Card>
  );
};

const PriceTile = ({ icon, label, value, sub, testid }) => (
  <div className="rounded-md border border-gray-200 bg-white p-3">
    <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-1">
      {icon}
      {label}
    </div>
    <div className="text-lg font-semibold text-gray-900" data-testid={testid}>{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

export default NegotiationIntelligence;
